import { ChangeEvent, HTMLAttributes, ReactNode, createContext, useContext, useState } from 'react';
import { MouseEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import { SelectOption } from '@packages/types';

import { Button, ButtonProps } from '@elements/button';
import { Input, InputProps } from '@elements/input';
import { Loader } from '@elements/loader';
import { Text } from '@elements/text';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { sluggify, testProps } from '@utils';

const DROPDOWN_OPTION_HEIGHT = 40; // height of each option including inner padding
const DROPDOWN_OPTION_BOTTOM_SPACE = 8; // margin bottom of each option
const DROPDOWN_OPTION_CONTAINER_TOP_BOTTOM_SPACE = 24; // main container padding top and bottom i.e. s12 * 2

export type DropdownProps = {
  items: SelectOption[];
  onSelect: (value: unknown) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  inputProps?: InputProps;
  initialValue?: SelectOption;
  disabled?: boolean;
  optionsHeader?: ReactNode;
  isLoading?: boolean;
  numberOfItems?: number;
  customOptions?: ReactNode;
  hideSelectedLabel?: boolean;
  dropdownMaxHeight?: number;
} & HTMLAttributes<HTMLDivElement>;

const dropdownOptionsStyle = tv({
  base: 'absolute z-20 w-full overflow-auto rounded-2xl bg-surface-primary p-s12 mt-s8 shadow-bottom',
});

type DropdownContextType = DropdownProps & {
  filteredItems: SelectOption[];
  query: string;
  setQuery: (query: string) => void;
  handleInputValidate: () => void;
};

const DropdownContext = createContext<DropdownContextType>({} as DropdownContextType);
export const Dropdown = (props: DropdownProps) => {
  const {
    items,
    onBlur,
    disabled,
    inputProps,
    initialValue,
    className,
    numberOfItems = 5,
    dropdownMaxHeight,
    onSelect,
    hideSelectedLabel = false,
  } = props;
  const [query, setQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<SelectOption | null>(initialValue ?? null);
  const dropdownHeight =
    dropdownMaxHeight ??
    numberOfItems * DROPDOWN_OPTION_HEIGHT +
      numberOfItems * DROPDOWN_OPTION_BOTTOM_SPACE +
      DROPDOWN_OPTION_CONTAINER_TOP_BOTTOM_SPACE;

  const filteredItems =
    query === ''
      ? items
      : items.filter(item => {
          return item.label.toLowerCase().includes(query.toLowerCase());
        });

  const handleInputValidate = () => {
    const isMatch = items.find(item => item.label.toLowerCase() === query.toLowerCase());

    if (!isMatch) {
      setQuery('');
    }
  };

  return (
    <DropdownContext.Provider
      value={{ ...props, filteredItems, query, setQuery, handleInputValidate }}>
      <Combobox
        onBlur={onBlur}
        disabled={disabled}
        as="div"
        className={twMerge('relative', className)}
        value={selectedItem}
        onChange={data => {
          if (!data) return;
          setQuery('');
          setSelectedItem(data);
          onSelect(data.value);
        }}>
        <ComboboxInput
          as={Input}
          autoComplete="off"
          {...inputProps}
          disabled={disabled}
          onBlur={handleInputValidate}
          onChange={event => {
            setQuery(event.target.value);
            inputProps?.onChange?.(event);
          }}
          displayValue={item => (hideSelectedLabel ? '' : item?.label)}
          defaultValue={initialValue}
          rightIcon={(props: ButtonProps) => <RenderRightIcon {...props} />}
          {...testProps('dropdown-input')}
        />
        {/* Dropdown options */}

        <ComboboxOptions
          style={{ maxHeight: `${dropdownHeight}px` }}
          className={dropdownOptionsStyle()}>
          <OptionHeader />
          <OptionLoader />
          <OptionsList />
          <CustomOption />
        </ComboboxOptions>
      </Combobox>
    </DropdownContext.Provider>
  );
};

Dropdown.Button = ComboboxButton;
Dropdown.Option = ComboboxOption;

const OptionHeader = () => {
  const { optionsHeader } = useContext(DropdownContext);
  if (!optionsHeader) return null;
  return <>{optionsHeader}</>;
};

const OptionLoader = () => {
  const { isLoading } = useContext(DropdownContext);
  if (!isLoading) return null;
  return (
    <ComboboxOption value="test">
      <Loader className="mx-auto flex items-center justify-center w-s32 text-icon-brand-1" />
    </ComboboxOption>
  );
};

const dropdownOptionStyle = tv({
  base: 'py-s8 px-s12 mb-s8 last:mb-0 cursor-pointer hover:bg-surface-action-selected rounded-md border border-transparent',
  variants: {
    selected: {
      true: 'border-border-action-active bg-surface-action-selected hover:border-transparent',
    },
  },
});

const OptionsList = () => {
  const { isLoading, customOptions, filteredItems } = useContext(DropdownContext);
  if (filteredItems.length < 1 && !isLoading)
    return (
      <Text variant="body-16/r" className="px-s12 py-s8">
        No results found
      </Text>
    );

  if (customOptions) return null;

  return (
    <>
      {filteredItems.map(item => (
        <ComboboxOption
          key={item.id}
          value={item}
          className={({ selected }) => dropdownOptionStyle({ selected })}>
          <Text
            {...testProps(`label-${sluggify(item.label)}`)}
            variant="body-16/r"
            className="truncate text-body text-left">
            {item.label}
          </Text>
        </ComboboxOption>
      ))}
    </>
  );
};

const RenderRightIcon = (props: ButtonProps) => {
  const { inputProps } = useContext(DropdownContext);

  if (typeof inputProps?.rightIcon === 'function') {
    const RightIconComponent = inputProps.rightIcon;

    if (RightIconComponent.name === 'DropdownButton') {
      return <ComboboxButton as={Button} icon="arrow_drop_down" {...props} />;
    }

    if (RightIconComponent.name === 'DropdownClearButton') {
      return <DropdownClearButton icon="cancel" {...props} />;
    }
    return <RightIconComponent {...props} />;
  }
  return <>{inputProps?.rightIcon}</>;
};

const DropdownClearButton = (props: ButtonProps) => {
  const { query, setQuery } = useContext(DropdownContext);
  if (query.length < 1) return null;

  return (
    <Button
      icon="cancel"
      {...props}
      onClick={() => {
        setQuery('');
      }}
    />
  );
};
Dropdown.ClearButton = DropdownClearButton;

const DropdownButton = ({ onClick, ...rest }: ButtonProps) => {
  const { handleInputValidate } = useContext(DropdownContext);
  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    handleInputValidate();
  };
  return (
    <ComboboxButton as={Button} {...rest} onClick={handleButtonClick} icon="arrow_drop_down" />
  );
};
Dropdown.Button = DropdownButton;

const CustomOption = () => {
  const { customOptions } = useContext(DropdownContext);
  if (!customOptions) return null;
  return <>{customOptions}</>;
};
